import type {
  CreateVideoRequest,
  CreateVideoMetadata,
  UploadItem,
  DashboardUploaderS3Cfg,
} from '@/lib/types/Upload';
import type { IngestcfgData } from '@/lib/types/Ingest';
import { FilenameParser } from './FilenameParser';
import { CREDENTIAL_EXPIRATION_BUFFER_SECS } from '@/lib/constants/upload';

export class UploadUtils {
  /**
   * Build CreateVideoRequest from UploadItem + IngestcfgData.
   * Reference: lib2/model/media/CreateMediaReq.ts -> createVideoReqFromFilename()
   *
   * Published date priority chain (highest wins):
   *   filename-parsed date > ingestcfg.mediaSettings.published > file.lastModified
   */
  static buildCreateVideoRequest(
    item: UploadItem,
    ingestcfg: IngestcfgData
  ): CreateVideoRequest {
    const mediaSettings = ingestcfg.mediaSettings;
    const parsedMetadata = FilenameParser.parseMetadata(item.filename, item.lastModified ?? undefined);

    // Published priority chain: lastModified -> mediaSettings -> parser
    let published: number | null = item.lastModified
      ? Math.round(item.lastModified / 1000)
      : null;
    published = mediaSettings.published ?? published;
    published = parsedMetadata.published ?? published;

    const metadata: CreateVideoMetadata = {
      adSupported: !!mediaSettings.adSupported,
      availStart: mediaSettings.availStart || null,
      availEnd: mediaSettings.availEnd || null,
      published,
      refId: parsedMetadata.refId,
      tags: item.tags.length > 0 ? item.tags : ingestcfg.tags,
      title: parsedMetadata.title || item.title,
    };

    return {
      mediaStateType: 'active',
      ingestMethodType: 'dashboard',
      ingestcfgId: ingestcfg.ingestcfgId,
      mediaSourceType: 'uploaded_source',
      mediaType: 'video',
      srcFilename: item.filename,
      metadata,
    };
  }

  /**
   * Check if STS credentials are still valid (with 6hr buffer).
   * Returns false if credentials will expire within the buffer period.
   */
  static areCredentialsValid(cfg: DashboardUploaderS3Cfg): boolean {
    const nowSecs = Math.floor(Date.now() / 1000);
    return cfg.expirationTS - nowSecs > CREDENTIAL_EXPIRATION_BUFFER_SECS;
  }

  /**
   * Human-readable file size.
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  /**
   * Derive MIME type from filename extension.
   * Falls back to 'application/octet-stream' for unknown types.
   */
  static getContentType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeMap: Record<string, string> = {
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      mkv: 'video/x-matroska',
      webm: 'video/webm',
      m4v: 'video/x-m4v',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
      mpg: 'video/mpeg',
      mpeg: 'video/mpeg',
    };
    return (ext && mimeMap[ext]) || 'application/octet-stream';
  }
}
